import { Checkbox, Divider, Form, Input, message, Radio, Spin, Tooltip, Typography } from 'antd';
import PageMeta from 'components/PageMeta/PageMeta';
import { Information } from '@carbon/icons-react';
import { Banner } from 'components/ui/Banner';
import { getPhoneNumberValidator, isPhoneNumberValid, validateEmail } from 'utils';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PhoneNumber from 'components/PhoneNumber';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getSpouseTitle } from './constants';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { useSelector } from 'react-redux';
import { selectors } from 'features/auth';

export function FamilyApplicationSpouseContactInfo({
  application,
  setApplication,
  household,
  setStep,
  loading,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const currentUser = useSelector(selectors.selectUser);

  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;
  const spouseTitle = getSpouseTitle(application);

  // Form state
  const [form] = Form.useForm();
  const emailFormValue = Form.useWatch('email', form);
  const phoneFormValue = Form.useWatch('phone', form);
  const contactInfoWithheld = Form.useWatch('contactInfoWithheld', form);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            email: values?.contactInfoWithheld ? null : values?.email,
            phone: values?.contactInfoWithheld ? null : values?.phone,
            phoneType: values?.contactInfoWithheld ? null : values?.phoneType,
            contactInfoWithheld: values?.contactInfoWithheld ?? false,
          },
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.additionalParents?.[0] !== undefined) {
      form.setFieldsValue({
        email: application?.additionalParents?.[0]?.email || (!isCompletedBySponsor ? currentUser?.email : null),
        phone: application?.additionalParents?.[0]?.phone,
        phoneType: application?.additionalParents?.[0]?.phoneType,
        contactInfoWithheld: application?.additionalParents?.[0]?.contactInfoWithheld ?? false,
      });
    }
  }, [application, currentUser, form, isCompletedBySponsor]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Documents" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7">
          <Banner
            question={
              isCompletedBySponsor
                ? `How would you like us to contact ${getOtherAdultFirstName(application?.additionalParents?.[0])}?`
                : 'How would you like us to contact you?'
            }
            guidance={isCompletedBySponsor || spouseTitle === 'spouse' ? null : `As the ${spouseTitle}`}
            className="mb-12"
          />

          {isCompletedBySponsor && (
            <>
              <Form.Item name="contactInfoWithheld" valuePropName="checked" className="mb-0">
                <Checkbox>I do not wish to provide this information</Checkbox>
              </Form.Item>
            </>
          )}

          {!contactInfoWithheld && (
            <>
              {isCompletedBySponsor && <Divider className="mt-6 mb-8" />}

              {/*
               * Email input
               */}
              <Form.Item
                label={
                  <div className="flex items-center gap-2">
                    {isCompletedBySponsor ? `Their email address` : 'Your primary email address'}{' '}
                    <Tooltip title="This email is used for official notifications and detailed information about the application status and benefits. It's important to provide an email that is checked regularly.">
                      <Information />
                    </Tooltip>
                  </div>
                }
                name="email"
                rules={[
                  {
                    required: true,
                    message: `Please enter ${isCompletedBySponsor ? `their` : 'your'} email address`,
                  },
                  {
                    type: 'email',
                    message: `Please enter a valid email address`,
                  },
                ]}
                className="mb-0"
              >
                <Input />
              </Form.Item>

              <Typography.Text type="secondary" className="block mt-2 max-w-[800px]">
                Use a personal email address that is checked often
              </Typography.Text>

              {emailFormValue && validateEmail(emailFormValue) && (
                <>
                  {/*
                   * Phone input
                   */}
                  <Form.Item
                    label={
                      <div className="flex items-center gap-2">
                        {isCompletedBySponsor ? `Their phone number` : 'Your phone number'}{' '}
                        <Tooltip title="This phone number will be used when we need to quickly reach someone about the application. Please provide a number that is monitored regularly.">
                          <Information />
                        </Tooltip>
                      </div>
                    }
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: `Please enter ${isCompletedBySponsor ? 'their' : 'your'} phone number`,
                      },
                      getPhoneNumberValidator('Invalid phone number'),
                    ]}
                    validateFirst
                    className="mt-8 mb-0"
                  >
                    <PhoneNumber />
                  </Form.Item>

                  {phoneFormValue && isPhoneNumberValid(phoneFormValue) && (
                    <Form.Item
                      name="phoneType"
                      label="What kind of phone is this?"
                      rules={[
                        {
                          required: true,
                          message: 'Please select a phone type',
                        },
                      ]}
                      className="mt-8 mb-0"
                    >
                      <Radio.Group
                        block
                        optionType="button"
                        buttonStyle="solid"
                        className="flex flex-wrap"
                        data-testid="phone-type-group"
                      >
                        {phoneTypeOptions.map((option) => (
                          <Radio.Button
                            key={option.value}
                            value={option.value}
                            className="flex-[unset] min-w-[90px]"
                            data-testid={`phone-type-${option.value.toLowerCase()}`}
                          >
                            {option.title}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </>
  );
}

// Form options
const phoneTypeOptions = [
  {
    title: 'Cell',
    value: 'Cell',
  },
  {
    title: 'Home',
    value: 'Home',
  },
  {
    title: 'Work',
    value: 'Work',
  },
];
